/**
 * Colormaps for false-colour display. Perceptually uniform maps are defined by
 * anchor colours sampled from the matplotlib originals and interpolated in RGB;
 * Turbo uses Google's published polynomial approximation.
 */

export type ColormapId = 'gray' | 'viridis' | 'inferno' | 'magma' | 'plasma' | 'cividis' | 'turbo' | 'jet';

export const COLORMAPS: { id: ColormapId; label: string }[] = [
  { id: 'gray', label: 'Grayscale' },
  { id: 'viridis', label: 'Viridis' },
  { id: 'inferno', label: 'Inferno' },
  { id: 'magma', label: 'Magma' },
  { id: 'plasma', label: 'Plasma' },
  { id: 'cividis', label: 'Cividis (colour-blind safe)' },
  { id: 'turbo', label: 'Turbo (rainbow)' },
  { id: 'jet', label: 'Jet (legacy rainbow, LMK-style)' },
];

const ANCHORS: Record<string, string[]> = {
  viridis: ['#440154', '#472d7b', '#3b528b', '#2c728e', '#21918c', '#28ae80', '#5ec962', '#addc30', '#fde725'],
  inferno: ['#000004', '#1f0c48', '#550f6d', '#88226a', '#ba3655', '#e35933', '#f9950a', '#f8c932', '#fcffa4'],
  magma: ['#000004', '#1c1044', '#4f127b', '#812581', '#b5367a', '#e55064', '#fb8761', '#fec287', '#fcfdbf'],
  plasma: ['#0d0887', '#4c02a1', '#7e03a8', '#a92395', '#cc4778', '#e56b5d', '#f89441', '#fdc328', '#f0f921'],
  cividis: ['#00224e', '#123570', '#3b496c', '#575d6d', '#707173', '#8a8779', '#a69d75', '#c4b56c', '#e4cf5b', '#fee838'],
};

function hex(h: string): [number, number, number] {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}

function turbo(x: number): [number, number, number] {
  const r = 0.13572138 + x * (4.6153926 + x * (-42.66032258 + x * (132.13108234 + x * (-152.94239396 + x * 59.28637943))));
  const g = 0.09140261 + x * (2.19418839 + x * (4.84296658 + x * (-14.18503333 + x * (4.27729857 + x * 2.82956604))));
  const b = 0.1066733 + x * (12.64194608 + x * (-60.58204836 + x * (110.36276771 + x * (-89.90310912 + x * 27.34824973))));
  const c = (v: number) => Math.round(255 * Math.min(1, Math.max(0, v)));
  return [c(r), c(g), c(b)];
}

function jet(x: number): [number, number, number] {
  const c = (v: number) => Math.round(255 * Math.min(1, Math.max(0, v)));
  return [c(1.5 - Math.abs(4 * x - 3)), c(1.5 - Math.abs(4 * x - 2)), c(1.5 - Math.abs(4 * x - 1))];
}

const cache = new Map<string, Uint8Array>();

/**
 * 256-entry RGB lookup table. With `bands` > 1 the map is quantised into that
 * many flat colour steps (iso-level banding).
 */
export function colormapLut(id: ColormapId, bands = 0): Uint8Array {
  const key = `${id}:${bands}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const lut = new Uint8Array(256 * 3);
  for (let i = 0; i < 256; i++) {
    let x = i / 255;
    if (bands > 1) x = Math.min(bands - 1, Math.floor(x * bands)) / (bands - 1);
    let c: [number, number, number];
    if (id === 'gray') c = [Math.round(x * 255), Math.round(x * 255), Math.round(x * 255)];
    else if (id === 'turbo') c = turbo(x);
    else if (id === 'jet') c = jet(x);
    else {
      const a = (ANCHORS[id] ?? ANCHORS.viridis).map(hex);
      const p = x * (a.length - 1);
      const k = Math.min(a.length - 2, Math.floor(p));
      const t = p - k;
      c = [0, 1, 2].map((j) => Math.round(a[k][j] + t * (a[k + 1][j] - a[k][j]))) as [number, number, number];
    }
    lut.set(c, i * 3);
  }
  cache.set(key, lut);
  return lut;
}

export function colormapCss(id: ColormapId, x: number, bands = 0): string {
  const lut = colormapLut(id, bands);
  const i = Math.round(Math.min(1, Math.max(0, x)) * 255) * 3;
  return `rgb(${lut[i]},${lut[i + 1]},${lut[i + 2]})`;
}
